import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  Fade,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import { AddRounded as AddIcon } from '@mui/icons-material';
import { Recipient, RecipientRole } from 'features/document/constants';
import { getRandomHexColor } from 'utils/getRandomHexColor';
import Button from 'components/Button/Button';
import { CloseRounded, DeleteRounded, CheckRounded } from '@mui/icons-material';
import { docGeniusPalette } from 'styles/partials/colors';
import style from './EditRecipientModal.module.css';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { isValidEmail } from 'utils/emailValidation';
import { recipientRoleOptions } from './recipientRoleOptions';
import { updateRecipients } from 'features/document/documentSlice';
import RecipientsAutocomplete, { RecipientOption } from './RecipientAutocomple.tsx/RecipientAutocomplete';
import { useGetContactsAndUsersQuery } from 'features/salesforce/salesforceApi';
import { selectIsSalesforceAuth } from 'features/salesforce/salesforceSlice';
import { QueryBuilder } from 'utils/formatQueryParams';
import { debounce, differenceWith, isEqual } from 'lodash';

interface EditRecipientModalProps {
  open: boolean;
  recipients: Recipient[];
  onClose: () => void;
  unSelectSigner: () => void;
}

const emptyRecipient: Recipient = {
  id: '',
  fullName: '',
  elementsIds: [],
  email: '',
  role: RecipientRole.SIGNER,
  color: getRandomHexColor(),
};

export const EditRecipientModal = ({ open, recipients, onClose, unSelectSigner }: EditRecipientModalProps) => {
  const dispatch = useAppDispatch();
  const selector = useAppSelector;

  const isSalesforceAuth = selector(selectIsSalesforceAuth);
  const salesforceBaseUrl = selector(state => state.auth.salesforceBaseUrl);
  const [documentRecipients, setNewDocumentRecipients] = useState<Recipient[]>(
    recipients.length ? recipients : [emptyRecipient]
  );
  const [recipientOptions, setRecipientOptions] = useState<RecipientOption[]>(
    mapDocumentRecipientsToOptions(recipients)
  );
  const [selectedRecipients, setSelectedRecipients] = useState<RecipientOption[]>([]);

  const [query, setQuery] = useState('');
  const builder = new QueryBuilder();
  const queryString = builder.setQueryLimit({ value: 5 });
  if (query) {
    builder.setQueryParam({ field: 'name', value: `${query}` });
  }

  const { data: dataContactsAndUsers, isFetching } = useGetContactsAndUsersQuery(queryString.build(), {
    skip: !isSalesforceAuth,
  });

  function mapDocumentRecipientsToOptions(documentRecipients: Recipient[]) {
    return documentRecipients.map(({ id, email, fullName }) => ({
      id,
      email,
      name: fullName,
    }));
  }

  useEffect(() => {
    const mappedDocumentRecipients = mapDocumentRecipientsToOptions(documentRecipients);
    setSelectedRecipients(mappedDocumentRecipients);
  }, [documentRecipients]);

  useEffect(() => {
    if (dataContactsAndUsers) {
      setRecipientOptions([...mapDocumentRecipientsToOptions(recipients), ...dataContactsAndUsers]);
    }
  }, [dataContactsAndUsers]);

  useEffect(() => {
    setNewDocumentRecipients(recipients.length ? recipients : [emptyRecipient]);
  }, [recipients]);

  const isEmailValid = (email: string) => isValidEmail(email);

  const isRecipientValid = (recipient: Recipient) => {
    return recipient.fullName.trim() !== '' && recipient.email.trim() !== '' && isEmailValid(recipient.email);
  };

  const debouncedFetchResults = debounce(searchQuery => {
    setQuery(searchQuery);
  }, 500);

  const handleSearchChange = (value: string) => {
    debouncedFetchResults(value);
  };

  const handleAddRecipient = () => {
    const newRecipient = {
      id: '',
      fullName: '',
      elementsIds: [],
      email: '',
      role: RecipientRole.SIGNER,
      color: getRandomHexColor(),
    };
    setNewDocumentRecipients([...documentRecipients, newRecipient]);
    handleSearchChange('');
  };

  const handleDeleteRecipient = (index: number) => {
    const updatedRecipients = [...documentRecipients];
    updatedRecipients.splice(index, 1);
    setNewDocumentRecipients(updatedRecipients);
    unSelectSigner();
  };

  const handleSaveRecipients = () => {
    dispatch(updateRecipients(documentRecipients));
    onClose();
  };

  const handleOnClose = () => {
    onClose();
    setNewDocumentRecipients(recipients.length ? recipients : [emptyRecipient]);
  };

  const handleOnChangeRecipient = (selectedRecipient: RecipientOption | null, recipientIndex: number) => {
    if (selectedRecipient) {
      const { name: fullName, id, email } = selectedRecipient;
      const updatedRecipients = documentRecipients.map((recipient, index) =>
        index === recipientIndex ? { ...recipient, fullName, id, email } : recipient
      );
      setNewDocumentRecipients(updatedRecipients);
    }
  };

  const handleOnChangeRole = (newRole: RecipientRole, recipientIndex: number) => {
    const updatedRecipients = documentRecipients.map((recipient, index) =>
      index === recipientIndex ? { ...recipient, role: newRole } : recipient
    );
    setNewDocumentRecipients(updatedRecipients);
  };

  const getAvailableRecipients = () => {
    return differenceWith(recipientOptions, selectedRecipients, isEqual);
  };

  const openInNewTab = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby='add-new-recipient' closeAfterTransition>
      <Fade in={open}>
        <Box className={style.modalContainer}>
          <Box className={style.modalHeader}>
            <Typography className={style.modalTitle}>Edit Recipients</Typography>
            <IconButton aria-label='close-modal' onClick={handleOnClose}>
              <CloseRounded sx={{ color: docGeniusPalette.primaryBlue }} />
            </IconButton>
          </Box>
          <Box
            className={style.modalSubtitleContainer}
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Typography
              className={style.modalSubtitle}
              fontSize={'16px'}
              fontWeight={'400'}
              color={'#474747'}
              mb={'12px'}
            >
              Edit the recipient(s) information and select their action.
            </Typography>

            <Button
              disabled={!salesforceBaseUrl}
              variant='tertiary'
              sx={{ width: '252px' }}
              onClick={() => openInNewTab(`${salesforceBaseUrl}/lightning/o/Contact/new?count=1`)}
            >
              <AddIcon className={style.addIcon} />
              Create Contact In CRM
            </Button>
          </Box>
          <Box className={style.modalBody}>
            {documentRecipients.map((recipient, index) => {
              const isDisabled = recipient.elementsIds && recipient.elementsIds.length > 0;
              const filteredOptions = getAvailableRecipients();
              return (
                <Box alignItems={'center'} display={'flex'} key={`recipient-${index}`} gap={'16px'}>
                  <RecipientsAutocomplete
                    label='Name'
                    disabled={isDisabled}
                    loading={isFetching}
                    options={filteredOptions as RecipientOption[]}
                    value={recipient.id ? { id: recipient.id, name: recipient.fullName, email: recipient.email } : null}
                    onInputChange={function (event: React.SyntheticEvent, value: string) {
                      if (value !== '') {
                        handleSearchChange(value);
                      }
                    }}
                    onChange={function (event: React.SyntheticEvent, value: RecipientOption | null) {
                      handleOnChangeRecipient(value, index);
                    }}
                  />
                  <TextField
                    label='Email'
                    type='email'
                    value={recipient.email}
                    fullWidth
                    disabled
                    InputProps={{ sx: { borderRadius: '8px', height: '48px' } }}
                  />
                  <FormControl fullWidth>
                    <InputLabel id={`role-select-label-${index}`}>Action Required</InputLabel>
                    <Select
                      labelId={`role-select-label-${index}`}
                      label='Action Required'
                      disabled={isDisabled}
                      id={`role-select-${index}`}
                      sx={{ height: '48px' }}
                      value={recipient.role}
                      onChange={e => {
                        handleOnChangeRole(e?.target.value as RecipientRole, index);
                      }}
                    >
                      {recipientRoleOptions.map(({ value, label, Icon }, index) => (
                        <MenuItem
                          key={index}
                          value={value}
                          sx={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px' }}
                        >
                          <Box alignItems={'center'} display={'flex'} gap='8px'>
                            {Icon && <Icon />}
                            {label}
                          </Box>
                          {recipient.role === value && <CheckRounded sx={{ color: docGeniusPalette.primaryBlue }} />}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {isDisabled ? (
                    <Tooltip placement='top' title='Recipient has linked elements'>
                      <span>
                        <IconButton aria-label={`delete-${recipient.fullName}`} disabled={isDisabled}>
                          <DeleteRounded sx={{ color: '#717273' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title='Delete Recipient' arrow>
                      <IconButton
                        aria-label={`delete-${recipient.fullName}`}
                        onClick={() => handleDeleteRecipient(index)}
                      >
                        <DeleteRounded sx={{ color: docGeniusPalette.primaryBlue }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
          <Box alignItems={'center'} display={'flex'} paddingLeft={'40px'}>
            <Button variant='tertiary' onClick={handleAddRecipient} sx={{ width: '219px' }}>
              Add Another Recipient
            </Button>
          </Box>
          <Box className={style.modalFooter}>
            <Button className={style.secondaryBtn} variant='tertiary' onClick={handleOnClose}>
              Cancel
            </Button>
            <Button
              className={style.primaryBtn}
              disabled={!documentRecipients.every(isRecipientValid)}
              onClick={handleSaveRecipients}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};
